import { BitFlagHelper } from '@core/helpers/enum-helper';

import { DealChecklistType, FIMenuSection, PersonType } from '@core/classes/SharedEnums';
import { IDocumentVerification, IUploadedDocument } from '@core/classes/UploadedDocument';
import SettingsChecklistItem, { IChecklistSectionDependency } from '@core/classes/Checklist/SettingsChecklistItem';
import DocumentAssociation from '@core/classes/DocumentAssociation';
import ENUMS from '@core/classes/Enums';
import { IChecklistFieldToMatchValue } from '@core/classes/Checklist/ChecklistFieldToMatch';
import { PlaidCustomerData } from '@core/classes/Plaid/PlaidCustomerData';
import { SaturnPermissions } from '@core/classes/AccessEmployeeClass';

export enum ChecklistStatus {
	Error = 'ERROR',
	Pending = 'PENDING',
	Rejected = 'REJECTED',
	Approved = 'APPROVED',
	Unverified = 'UNVERIFIED',
}

export enum ChecklistVerifyType {
	Required,
	Optional,
	Both,
}

export interface IChecklistErrorOverview {
	hasErrors: boolean;
	criticalErrors: number;
	minorErrors: number;
	fieldErrors: number;
}

export interface IChecklistVerification {
	/** Whether or not this checklist item has been fulfilled */
	isVerified: boolean;

	/** Whether or not isVerifiedExpression returned true */
	autoVerified: boolean;

	/** List of employees who have submitted a manual verification for this checklist item. */
	manuallyVerifiedBy: SaturnPermissions;

	/** When true, means that a reviewer has manually rejected the results of this item. Signals that this item needs to be retried. */
	isManuallyRejected: boolean;

	/** The UploadedDocument returned by isVerifiedExpression. May not be available for all checklist types. */
	document: IUploadedDocument;

	/** The PlaidCustomerData returned by isVerifiedExpression. May not be available for all checklist types. */
	plaidData: PlaidCustomerData;
}

export default class DealChecklistItem {
	/** GUID | ElasticSearch ID of the related SettingsChecklistItem (for custom items, GUID is randomly generated) */
	id: string;

	/** When true, indicates that this is a custom item made on FI Menu by a deal employee */
	isCustomItem: boolean;

	/** When this item was generated as part of a Checklist Set, the index of this item. Otherwise, will be `null` */
	setIndex?: number;

	/** Array of items associated with this checklist item */
	associations: DocumentAssociation[];

	/** Display name of this checklist item */
	name: string;

	/** Description of this checklist item */
	description: string;

	/** If empty, assume that the deal employee is responsible for completing the item */
	sendTo: PersonType[];

	/** The type of action that is needed in order to fulfill this checklist item. */
	type: DealChecklistType;

	/** Some checklist types have specific subtypes (ex. for file uploads, the subtype is the document type) */
	subtype?: string;

	/** If set, prevents FI Menu from proceeding to the next section until this checklist item is fulfilled */
	sectionDependency: FIMenuSection;

	/** A list of all section dependencies set on this item. */
	allSectionDependencies: IChecklistSectionDependency[];

	/** Used for specific Checklist types that require extra config fields */
	metadata: { key: string; value: unknown }[];

	/** Contains information about how this item was verified */
	status: IChecklistVerification;

	/** Bit flag of employees that are required to give a manual verification of this item. */
	needsVerifyBy: SaturnPermissions;

	/** Bit flag of employees that can optionally provide a manual verification of this item. */
	optionalVerifyBy: SaturnPermissions;

	/** Used in manual verification of documents */
	fieldsToMatch?: IChecklistFieldToMatchValue[];

	/** When populated, indicates that an error occurred while trying to generate this checklist item. */
	error?: {
		type: string;
		message: string;
		stackTrace?: string;
	};

	constructor(init?: Partial<DealChecklistItem>) {
		Object.assign(
			this,
			{
				associations: [],
				sendTo: [],
				type: DealChecklistType.Other,
				sectionDependency: FIMenuSection.Unknown,
				metadata: [],
				status: {
					isVerified: false,
					autoVerified: false,
					manuallyVerifiedBy: SaturnPermissions.None,
					isManuallyRejected: false,
				},
				needsVerifyBy: SaturnPermissions.None,
				optionalVerifyBy: SaturnPermissions.None,
				fieldsToMatch: [],
			},
			init,
		);
	}

	/** Takes the data in this class and generates a `SettingsChecklistItem` object. Will only populate properties the two classes have in common. */
	toSettingsChecklistItem(): SettingsChecklistItem {
		return new SettingsChecklistItem({
			id: this.id,
			name: this.name,
			description: this.description,
			isCustomItem: this.isCustomItem,
			sendTo: this.sendTo,
			associationInfo: this.associations.map(a => ({ type: a.type })),
			type: this.type,
			subtype: this.subtype,
			sectionDependencies: [
				{
					section: this.sectionDependency,
					requiredVerificationBy: this.needsVerifyBy,
					optionalVerificationBy: SaturnPermissions.None,
				},
			],
		});
	}

	// #region Checklist State
	/** Returns simplified status information for this checklist item. Reflects the item's overall status and is not specific to the user. */
	getOverallStatus(): ChecklistStatus {
		if (this.hasCriticalErrors()) return ChecklistStatus.Error;

		if (this.requiresManualVerification(ChecklistVerifyType.Required) && this.status.isManuallyRejected) return ChecklistStatus.Rejected;

		if (this.isPendingVerification(ChecklistVerifyType.Required) || this.isPlaidPending()) return ChecklistStatus.Pending;

		if (this.status.isVerified) return ChecklistStatus.Approved;

		return ChecklistStatus.Unverified;
	}

	/**
	 * Returns a status description for this checklist item, for display purposes. When `userPermissions` are null, you will get an overall status description.
	 * Otherwise, you'll get a status description that reflects whether this user still needs to take any specific actions to clear this checklist item.
	 */
	getStatusText(verificationType: ChecklistVerifyType, userPermissions?: SaturnPermissions): string {
		if (this.hasCriticalErrors()) return 'HAS CRITICAL ERRORS';

		if (this.isTypeUpload()) {
			if (!this.getDocument()) return 'NO DOCUMENT FOUND';
			else if (!this.status.autoVerified) return 'INVALID DOCUMENT';
		}

		// If this is an item eligible for manual verifications or the user is allowed to verify
		const canManuallyVerify = this.requiresManualVerification(verificationType) || (userPermissions != null && this.canUserVerify(verificationType, userPermissions));
		if (canManuallyVerify) {
			const isManuallyVerified = this.hasAllManualVerifications(verificationType) || (userPermissions != null && this.didUserVerify(userPermissions));

			if (this.status.isManuallyRejected) return 'MANUALLY REJECTED';
			else if (!this.hasAnyManualVerifications()) return 'SUBMITTED - UNVERIFIED';
			else if (isManuallyVerified) return 'MANUALLY VERIFIED';
			else return 'PARTIALLY VERIFIED';
		}

		// Plaid-specific status text
		if (this.isTypePlaid()) {
			if (!this.getPlaidData()) return 'NOT STARTED';
			else if (this.isPlaidPending()) return 'PENDING';
			else if (!this.status.autoVerified) return 'INVALID SESSION';
			else return 'VERIFIED BY PLAID';
		}

		// Default status text
		const isVerified = userPermissions != null ? this.isTaskCompleteForUser(verificationType, userPermissions) : this.isVerified();
		return isVerified ? 'VERIFIED' : 'NOT VERIFIED';
	}

	/** Returns a status description that can be shown to the customer, excluding any information that is irrelevant for customers to see. */
	getCustomerStatusText(): string {
		if (this.hasCriticalErrors()) return 'HAS CRITICAL ERRORS';
		else if (!this.isTypeUpload()) return this.getStatusText(ChecklistVerifyType.Required);
		else if (!this.getDocument()) return 'NO DOCUMENT FOUND';
		else if (!this.status.autoVerified) return 'INVALID DOCUMENT';
		else if (this.status.isManuallyRejected) return 'MANUALLY REJECTED';
		else return 'SUBMITTED';
	}

	/** Returns true if this item is fully verified. */
	isVerified(): boolean {
		return this.status.isVerified;
	}

	/** Tells you whether this item still needs action to be taken before it can be considered "completed". */
	isTaskCompleteForUser(verificationType: ChecklistVerifyType, userPermissions: SaturnPermissions): boolean {
		// Currently, the only thing a user can act on is a pending manual verification
		return this.status.autoVerified && !this.isPendingVerification(verificationType, userPermissions);
	}

	/** Returns true if there's nothing left for the customer to act on for this item. When false, indicates that the customer should redo this item. */
	isTaskCompleteForCustomer(): boolean {
		return !this.requiresManualVerification(ChecklistVerifyType.Both) ? this.status.autoVerified : this.status.autoVerified && !this.status.isManuallyRejected;
	}

	/** Compares the `status` object of this item with another item and returns true if there are any differences between the two statuses. */
	anyStatusChanges(newDealChecklistItem: DealChecklistItem): boolean {
		// Only runs check if the passed in DealChecklistItem has the same ID as this.id
		if (this.id !== newDealChecklistItem.id) return false;

		const currStatus = this.status;
		const newStatus = newDealChecklistItem.status;

		return (
			currStatus.autoVerified !== newStatus.autoVerified ||
			currStatus.isManuallyRejected !== newStatus.isManuallyRejected ||
			currStatus.isVerified !== newStatus.isVerified ||
			currStatus.manuallyVerifiedBy !== newStatus.manuallyVerifiedBy ||
			currStatus.document?.id !== newStatus.document?.id ||
			currStatus.plaidData?.id !== newStatus.plaidData?.id
		);
	}
	// #endregion

	// #region Checklist Errors
	/** Returns true if this item generated with *any* errors. */
	hasAnyErrors(): boolean {
		return this.hasCriticalErrors() || this.hasFieldErrors();
	}

	/**
	 * Returns true if a critical error was encountered when evaluating this checklist item. Indicates that we are unable to tell whether this item is
	 * required for this deal or has been fully verified. Checklist items with critical errors cannot be used or acted upon.
	 */
	hasCriticalErrors(): boolean {
		return this.error != null;
	}

	/**
	 * Returns true if one of the expressions in the fields-to-match list failed to evaluated. These errors are self-contained and do not interfere with
	 * normal checklist functionality.
	 */
	hasFieldErrors(): boolean {
		return this.fieldsToMatch.some(f => f.error != null);
	}
	// #endregion

	// #region Manual Verifications
	/** Appends a new manual verification to this checklist item, updating all other relevant properties. */
	addManualVerification(verificationType: ChecklistVerifyType, verification: IDocumentVerification) {
		if (!this.requiresManualVerification(verificationType)) throw new Error('This checklist item does not require manual verifications.');
		if (this.status.isManuallyRejected)
			throw new Error('This checklist item has been manually rejected. Please redo this checklist item before adding a manual verification.');

		this.status.manuallyVerifiedBy = verification.isVerified
			? BitFlagHelper.addFlag(this.status.manuallyVerifiedBy, verification.verifierPermissions)
			: BitFlagHelper.removeFlag(this.status.manuallyVerifiedBy, verification.verifierPermissions);

		this.status.isVerified = this.hasAllManualVerifications(ChecklistVerifyType.Required);
		this.status.isManuallyRejected = !verification.isVerified;

		const doc = this.getDocument();
		if (this.isTypeUpload() && doc) doc.docInfo.manualVerifications.push(verification);

		// TODO: In the future, may need to add extra handling for other checklist types that allow manual verification
	}

	getFieldsAsKeyValueList(): { key: string; value: string }[] {
		return this.fieldsToMatch.map(f => ({ key: f.name, value: f.fieldValue }));
	}

	/** Maps `ChecklistVerifyType` to a list of permissions (either the `needsVerifyBy` list,  the `optionalVerifyBy` list, or a combination of both lists). */
	getPermissionsSet(verificationType: ChecklistVerifyType): SaturnPermissions {
		switch (verificationType) {
			case ChecklistVerifyType.Both:
				return this.needsVerifyBy | this.optionalVerifyBy;
			case ChecklistVerifyType.Optional:
				return this.optionalVerifyBy;
			default:
				return this.needsVerifyBy;
		}
	}

	/** Returns the set of permissions that are required for manual verifications but haven't yet submitted a verification. */
	getMissingVerifications(verificationType: ChecklistVerifyType): SaturnPermissions {
		const permissionsToCheck = this.getPermissionsSet(verificationType);

		if (permissionsToCheck === SaturnPermissions.None) return SaturnPermissions.None;
		if (this.status.manuallyVerifiedBy === SaturnPermissions.None) return permissionsToCheck;

		return BitFlagHelper.removeFlag(permissionsToCheck, this.status.manuallyVerifiedBy);
	}

	/**
	 * Returns true if this item is awaiting manual verification. When you pass in `userPermission`, will check whether this specific user still needs
	 * to submit a manual verification.
	 */
	isPendingVerification(verificationType: ChecklistVerifyType, userPermissions?: SaturnPermissions): boolean {
		// When an item is not auto verified, indicates that it is not ready for manual verification until the item is retried
		if (!this.requiresManualVerification(verificationType) || !this.status.autoVerified) return false;

		const hasNoMissingVerifications = this.hasAllManualVerifications(verificationType);
		if (hasNoMissingVerifications) return false;

		// If no permissions are passed in, return whether this item is missing ANY of its required approvals
		if (userPermissions == null) return !hasNoMissingVerifications;

		// If we're in the required approvals list, return whether this item is missing OUR approval
		if (this.canUserVerify(verificationType, userPermissions)) return !this.didUserVerify(userPermissions);

		return false;
	}

	/** Returns true if this item has manual verifications enabled. */
	requiresManualVerification(verificationType: ChecklistVerifyType): boolean {
		return this.getPermissionsSet(verificationType) !== SaturnPermissions.None;
	}

	/** Returns true if the item has all the manual verifications that it requires. */
	hasAllManualVerifications(verificationType: ChecklistVerifyType): boolean {
		if (!this.requiresManualVerification(verificationType)) return true;

		const permissionsToCheck = this.getPermissionsSet(verificationType);
		return BitFlagHelper.contains(this.status.manuallyVerifiedBy, permissionsToCheck);
	}

	/** Returns true if this specific user can submit a manual verification on this item. */
	canUserVerify(verificationType: ChecklistVerifyType, userPermissions: SaturnPermissions): boolean {
		if (!this.requiresManualVerification(verificationType)) return false;

		const permissionsToCheck = this.getPermissionsSet(verificationType);
		return BitFlagHelper.hasIntersection(permissionsToCheck, userPermissions);
	}

	/** Returns true if someone with this user's credentials has already submitted a manual verification on this item. */
	didUserVerify(userPermissions: SaturnPermissions): boolean {
		if (userPermissions === SaturnPermissions.None) return false;
		return BitFlagHelper.contains(this.status.manuallyVerifiedBy, userPermissions);
	}

	/** Returns true if any manual verifications have been submitted on this item. */
	hasAnyManualVerifications(): boolean {
		return this.status.manuallyVerifiedBy !== SaturnPermissions.None;
	}
	// #endregion

	// #region Document Upload Items
	/** Gets the document stored in the status object (if any) */
	getDocument(): IUploadedDocument {
		return this.status.document;
	}

	/** Returns true if this item's `type` involves some kind of document upload. */
	isTypeUpload(): boolean {
		return SettingsChecklistItem.isDocumentAction(this.type);
	}

	/** If this item has a document in it's `status` object, returns true if that document's file type is some kind of image file. */
	isDocTypeImage(): boolean {
		const doc = this.getDocument();

		if (!doc) return false;
		return doc.docInfo.fileType.toLowerCase().indexOf('image') !== -1;
	}

	/** Returns true if a new document should be uploaded for this checklist item. */
	canUploadNewDocument(): boolean {
		if (!this.isTypeUpload()) return false;

		// Can upload if no document available or need to re-upload manually rejected document
		return !this.getDocument() || this.status.isManuallyRejected;
	}
	// #endregion

	// #region Plaid Items
	/** Gets the Plaid data stored in the status object (if any) */
	getPlaidData(): PlaidCustomerData {
		return this.status.plaidData;
	}

	/** Returns true if this item's `type` involves launching a Plaid Link session. */
	isTypePlaid(): boolean {
		return SettingsChecklistItem.isPlaidAction(this.type);
	}

	/** If the item's `status` object has Plaid data, returns whether Plaid has finished processing and retrieving that data. */
	isPlaidPending(): boolean {
		const plaidData = this.getPlaidData();
		if (!plaidData) return false;

		//The only products that would be pending are Payroll Income or Assets
		const plaidInteraction = plaidData.interactionType;
		if (plaidInteraction !== ENUMS.PLAID_INTERACTION.GetAssetReport && plaidInteraction !== ENUMS.PLAID_INTERACTION.GetPayrollIncome) return false;

		const error = plaidInteraction === ENUMS.PLAID_INTERACTION.GetPayrollIncome ? plaidData.payrollIncome.response.error : plaidData.assetReport.response.error;

		return error && error.errorCode === 'PRODUCT_NOT_READY';
	}

	/** Returns true if the item's `status` is in a state in which it's safe to reset the IDV session. Currently, we only allow resets if the IDV session has failed. */
	canResetPlaidIDV(): boolean {
		// Only allowing to reset IDV if auto verification fails
		if (this.type !== ENUMS.DealChecklistType.LaunchPlaid_IDV || !this.status.plaidData) return false;
		return !this.status.autoVerified;
	}
	// #endregion

	// #region Other Helpers
	/** If applicable, will try to return the timestamp stored in the status object. Not all checklist items will have a timestamp. */
	getTimestamp(): string {
		if (this.isTypePlaid() && this.status.plaidData) return this.status.plaidData.timestamp;
		if (this.isTypeUpload() && this.status.document) return this.status.document.docInfo.timestamp;
		return null;
	}

	/** If there's any metadata available, will return the value of the metadata item that matches the given key. */
	getMetadataValue(keyName: string): unknown {
		if (!this.metadata || this.metadata.length < 1) return null;
		return this.metadata.find(m => m.key === keyName)?.value ?? null;
	}

	/** Returns true if the person type is in this item's `sendTo` list. */
	canSendTo(personType: PersonType): boolean {
		return this.sendTo.some(p => p === personType);
	}

	/** Returns true if this item has data available to display. This is not connected to whether or not this item passed validation. */
	canDisplayResults(): boolean {
		return !!this.status?.document || !!this.status?.plaidData;
	}
	// #endregion
}
